import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { ensureJpegFromHeic } from '../../util/heicToJpeg';
import { approveCollectionPiece } from '../../util/api';
import {
  COLLECTION_LISTING_TYPE,
  collectionPublicData,
  getCollectionListingTypeConfig,
  visibleCollectionPieces,
} from '../../util/collections';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';

import { collectionImageFields, collectionImageParams } from '../CollectionPage/CollectionPage.duck';

// Stykker vises for ejeren, så snart de er gemt - også mens de (i det sjældne
// tilfælde hvor auto-godkendelsen fejler) venter på operatør-godkendelse.
const OWN_STATES = ['published', 'pendingApproval'];

// ================ Async Thunks ================ //

const queryOwnCollectionPayloadCreator = (
  { config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  return sdk.ownListings
    .query({
      states: OWN_STATES,
      perPage: 100,
      ...collectionImageParams(config?.layout?.listingImage, { includeAuthor: false }),
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      // Filtreres i klienten frem for i forespørgslen: en samler har få
      // annoncer, og vi undgår at afhænge af søgeindeks på egne listings.
      const pieceRefs = visibleCollectionPieces(response.data.data).map(({ id, type }) => ({
        id,
        type,
      }));
      return { pieceRefs };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const queryOwnCollectionThunk = createAsyncThunk(
  'MyCollectionPage/queryOwnCollection',
  queryOwnCollectionPayloadCreator
);

/**
 * Uploader ét foto og returnerer billed-id'et.
 * iPhone-fotos er ofte HEIC, som browsere ikke kan afkode - derfor samme
 * konvertering som i listing-wizarden.
 */
const uploadPieceImage = async (sdk, file, config) => {
  let usableFile = file;
  try {
    usableFile = await ensureJpegFromHeic(file);
  } catch (e) {
    usableFile = file;
  }
  // KUN felt-parametre her. images.upload svarer 500 hvis den får `include`.
  const response = await sdk.images.upload(
    { image: usableFile },
    { expand: true, ...collectionImageFields(config?.layout?.listingImage) }
  );
  return response.data.data.id;
};

const createPiecePayloadCreator = async (
  { title, description, files, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const listingTypeConfig = getCollectionListingTypeConfig(config?.listing?.listingTypes);
  if (!listingTypeConfig) {
    return rejectWithValue(
      storableError(new Error(`Listing type "${COLLECTION_LISTING_TYPE}" is not configured`))
    );
  }

  try {
    const imageIds = [];
    for (const file of files) {
      imageIds.push(await uploadPieceImage(sdk, file, config));
    }

    const response = await sdk.ownListings.create(
      {
        title,
        description,
        images: imageIds,
        publicData: collectionPublicData(listingTypeConfig),
      },
      { expand: true }
    );

    // Et samlingsstykke er ikke en vare, så det skal ikke igennem
    // operatør-godkendelse. Fejler godkendelsen, bliver stykket blot stående
    // som pendingApproval - det er synligt for ejeren og fanges af det
    // almindelige godkendelsesflow.
    const listingId = response?.data?.data?.id?.uuid;
    if (listingId) {
      try {
        await approveCollectionPiece(listingId);
      } catch (e) {
        // Bevidst tavs: stykket er gemt, kun synligheden udadtil forsinkes.
      }
    }

    await dispatch(queryOwnCollectionThunk({ config }));
    return response;
  } catch (e) {
    // Uden dette er alle fejl i kæden (upload, oprettelse, felter) ens udefra.
    log.error(e, 'collection-piece-create-failed', { title });
    return rejectWithValue(storableError(e));
  }
};

export const createPieceThunk = createAsyncThunk(
  'MyCollectionPage/createPiece',
  createPiecePayloadCreator
);

const updatePiecePayloadCreator = async (
  { listingId, title, description, keptImageIds, files, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  try {
    const newImageIds = [];
    for (const file of files || []) {
      newImageIds.push(await uploadPieceImage(sdk, file, config));
    }

    await sdk.ownListings.update(
      {
        id: listingId,
        title,
        description,
        images: [...keptImageIds, ...newImageIds],
      },
      { expand: true }
    );

    await dispatch(queryOwnCollectionThunk({ config }));
    return true;
  } catch (e) {
    log.error(e, 'collection-piece-update-failed', { listingId: listingId?.uuid });
    return rejectWithValue(storableError(e));
  }
};

export const updatePieceThunk = createAsyncThunk(
  'MyCollectionPage/updatePiece',
  updatePiecePayloadCreator
);

/**
 * Fjern et stykke fra samlingen. Sharetribe kan ikke hard-slette publicerede
 * listings, så vi bruger samme mønster som "Delete listing": luk den og flag
 * den med userDeleted, hvorefter den filtreres væk overalt.
 */
const removePiecePayloadCreator = async (
  { listingId, config },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  try {
    await sdk.ownListings.close({ id: listingId }, { expand: true });
    await sdk.ownListings.update(
      { id: listingId, publicData: { userDeleted: true } },
      { expand: true }
    );
    await dispatch(queryOwnCollectionThunk({ config }));
    return true;
  } catch (e) {
    log.error(e, 'collection-piece-remove-failed', { listingId: listingId?.uuid });
    return rejectWithValue(storableError(e));
  }
};

export const removePieceThunk = createAsyncThunk(
  'MyCollectionPage/removePiece',
  removePiecePayloadCreator
);

// ================ Slice ================ //

const initialState = {
  pieceRefs: [],
  queryError: null,
  queryInProgress: false,
  saveInProgress: false,
  saveError: null,
  removeInProgress: null,
  removeError: null,
};

const myCollectionPageSlice = createSlice({
  name: 'MyCollectionPage',
  initialState,
  reducers: {
    setInitialState: () => initialState,
    clearSaveError: state => {
      state.saveError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(queryOwnCollectionThunk.pending, state => {
        state.queryInProgress = true;
        state.queryError = null;
      })
      .addCase(queryOwnCollectionThunk.fulfilled, (state, action) => {
        state.queryInProgress = false;
        state.pieceRefs = action.payload.pieceRefs;
      })
      .addCase(queryOwnCollectionThunk.rejected, (state, action) => {
        state.queryInProgress = false;
        state.pieceRefs = [];
        state.queryError = action.payload;
      })
      .addCase(createPieceThunk.pending, state => {
        state.saveInProgress = true;
        state.saveError = null;
      })
      .addCase(createPieceThunk.fulfilled, state => {
        state.saveInProgress = false;
      })
      .addCase(createPieceThunk.rejected, (state, action) => {
        state.saveInProgress = false;
        state.saveError = action.payload;
      })
      .addCase(updatePieceThunk.pending, state => {
        state.saveInProgress = true;
        state.saveError = null;
      })
      .addCase(updatePieceThunk.fulfilled, state => {
        state.saveInProgress = false;
      })
      .addCase(updatePieceThunk.rejected, (state, action) => {
        state.saveInProgress = false;
        state.saveError = action.payload;
      })
      .addCase(removePieceThunk.pending, (state, action) => {
        state.removeInProgress = action.meta.arg.listingId?.uuid || action.meta.arg.listingId;
        state.removeError = null;
      })
      .addCase(removePieceThunk.fulfilled, state => {
        state.removeInProgress = null;
      })
      .addCase(removePieceThunk.rejected, (state, action) => {
        state.removeInProgress = null;
        state.removeError = action.payload;
      });
  },
});

export const { setInitialState, clearSaveError } = myCollectionPageSlice.actions;
export default myCollectionPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => dispatch => {
  dispatch(setInitialState());
  return Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(queryOwnCollectionThunk({ config })),
  ]);
};
